import { useCallback, useEffect, useId, useRef, useSyncExternalStore } from 'react';

let activePlayer = null;
const subscribers = new Set();
const stoppers = new Map();
const playbackLocks = new Set();
const subscribe = listener => { subscribers.add(listener); return () => subscribers.delete(listener); };
const snapshot = () => activePlayer;
function activate(id) {
  if (activePlayer === id) return;
  const previous = activePlayer;
  activePlayer = id;
  // Pause native media synchronously, before React commits the next player.
  // Iframes are removed by the same state update.
  stoppers.get(previous)?.();
  subscribers.forEach(listener => listener());
}

// Recording uses this coordinator too: playback must not reach the microphone.
export function stopAllPlayers() { activate(null); }

// Hold this for the whole capture session, including permission and preview
// preparation. A one-time pause lets another card play into the microphone.
export function acquirePlaybackLock() {
  const owner = {};
  playbackLocks.add(owner);
  activate(null);
  return () => { playbackLocks.delete(owner); };
}

export function isInteractiveCard(element) {
  if (playbackLocks.size || !element || !element.isConnected || document.hidden || element.closest('[inert], [hidden], [aria-hidden="true"]')
    || getComputedStyle(element).visibility === 'hidden') return false;
  for (let parent = element; parent; parent = parent.parentElement) {
    if (getComputedStyle(parent).display === 'none') return false;
  }
  const screen = element.closest('.news-screen');
  return !screen || screen.classList.contains('current-news');
}

export default function useSinglePlayer(onStop) {
  const id = useId();
  const stopRef = useRef(onStop);
  stopRef.current = onStop;
  const selected = useSyncExternalStore(subscribe, snapshot, () => null);
  const close = useCallback(() => { if (activePlayer === id) activate(null); }, [id]);
  const open = useCallback(() => { if (!playbackLocks.size) activate(id); }, [id]);
  useEffect(() => {
    stoppers.set(id, () => stopRef.current?.());
    const background = () => { if (document.hidden) close(); };
    document.addEventListener('visibilitychange', background);
    window.addEventListener('pagehide', close);
    window.addEventListener('popstate', close);
    return () => {
      close();
      stoppers.delete(id);
      document.removeEventListener('visibilitychange', background);
      window.removeEventListener('pagehide', close);
      window.removeEventListener('popstate', close);
    };
  }, [id, close]);
  return { active: selected === id, open, close };
}
